import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Badge } from '@/components/common/Badge'
import { Search, Clock, Eye, AlertCircle, FileText, Undo2 } from 'lucide-react'
import { useTokenStore, getConfirmedStorageKey } from '@/hooks/useTokenStore'
import { useTokenResolutionWorkflow } from '@/hooks/useTokenResolutionWorkflow'
import { SplitDialog, hasSplitPoints } from './SplitDialog'
import type { ConfirmedToken } from '@/utils/splitSuggestion'
import { MultiSplitDialog } from './MultiSplitDialog'
import { assignedSplit, canSplitToken, mergeSplitQueue } from '@/utils/tokenSplit'
import { loadSplitHints } from '@/utils/loadSplitHints'
import { withConfirmed } from '@/utils/splitSuggestion'
import { groupSplitParts, type PartFieldId, type SplitPartLookup } from '@/utils/splitPartGroups'
import { SplitPartAssignPanel } from './SplitPartAssignPanel'
import { CombineSimilarDialog } from './CombineSimilarDialog'
import { PdfPageViewer } from '@/components/pdf/PdfPageViewer'
import { loadTokenPdf } from '@/components/timetable-import/loadTokenPdf'
import { UnmatchedSubjectDialog } from '@/components/timetable-import/UnmatchedSubjectDialog'
import { UnmatchedBackgroundDialog } from '@/components/timetable-import/UnmatchedBackgroundDialog'
import { UnmatchedTeacherCodeDialog } from '@/components/timetable-import/UnmatchedTeacherCodeDialog'
import { BatchInfoHeader } from './BatchInfoHeader'
import { VirtualizedTokenList } from './VirtualizedTokenList'
import { TokenPagination } from './TokenPagination'
import type { BatchInfo } from './BatchInfoHeader'
import { tokenResolutionApi } from '@/services/api/tokenResolution'
import type { PdfPageResponse } from '@/types/tokenResolution'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'
import { deleteTokenShop } from '@/services/api/tokenShop'
import { applyBatchEdit } from '@/utils/batchEdit'
import type { TokenMatch, UnmatchedToken, TokenPositions, TokenOrigin } from '@/components/timetable-import/typesV2'
import type { TokenResolutionContext, ResolutionType } from '@/types/tokenResolution'
import { getOriginKey, getOriginLabel, getOriginDescription, shouldAutoConfirmByOrigin } from '@/utils/tokenOrigin'
import { shouldAutoConfirmVeryHighConfidence } from '@/utils/tokenConfidence'
import { knownTokenStatus } from '@/utils/tokenPatterns'
import { forgetIgnoredTokens, isIgnoredToken, knownIgnoredTokenNames, loadIgnoredTokenNames, overlayIgnoredNames, rememberIgnoredTokens } from '@/utils/ignoredTokens'
import { buildSubjectIdIndex, enrichSavedSubjects } from '@/utils/savedSubjectLink'
import { listSubjectAliases, listSubjects } from '@/services/api/qualification'

const FIELD_LABELS: Record<string, string> = {
  teacher_code: 'Lehrer-Code',
  teacher_name: 'Lehrer-Name',
  first_name: 'Vorname',
  family_name: 'Nachname',
  subject: 'Fach',
  room: 'Raum',
  class_name: 'Klasse',
  time_slot: 'Zeit',
  lesson_number: 'Lektion',
  ignore: 'Ignorieren',
  background: 'Hintergrund',
  split: 'Geteilt',
}

const FIELD_COLORS: Record<string, string> = {
  teacher_code: 'bg-blue-100 text-blue-800',
  teacher_name: 'bg-indigo-100 text-indigo-800',
  first_name: 'bg-purple-100 text-purple-800',
  family_name: 'bg-pink-100 text-pink-800',
  subject: 'bg-green-100 text-green-800',
  room: 'bg-yellow-100 text-yellow-800',
  class_name: 'bg-orange-100 text-orange-800',
  time_slot: 'bg-cyan-100 text-cyan-800',
  lesson_number: 'bg-slate-100 text-slate-700',
  ignore: 'bg-gray-100 text-gray-500',
  background: 'bg-teal-50 text-teal-700',
  split: 'bg-fuchsia-100 text-fuchsia-700',
}

const SOURCE_LABELS: Record<string, string> = {
  template_pattern_confirmed: 'Template',
  template_multi: 'Template',
  template_pattern_unconfirmed_structural: 'Template+Struktur',
  validator_exact_subject: 'DB',
  validator_room: 'DB',
  validator_class: 'DB',
  validator_teacher_code: 'DB',
  structural_pattern: 'Struktur',
  validated_pattern: 'Pattern',
  fallback: 'Fallback',
  // Normalized source values (from origin.source)
  header: 'Header',
  row_label: 'Zeile',
  cell_content: 'Zelle',
  cell_time: 'Zeit',
  filename: 'Datei',
  markdown: 'Markdown',
  template: 'Template',
  derived: 'Erkannt',
}

const SOURCE_COLORS: Record<string, string> = {
  template_pattern_confirmed: 'bg-purple-100 text-purple-700',
  template_multi: 'bg-purple-100 text-purple-700',
  template_pattern_unconfirmed_structural: 'bg-purple-50 text-purple-600',
  validator_exact_subject: 'bg-emerald-100 text-emerald-700',
  validator_room: 'bg-emerald-100 text-emerald-700',
  validator_class: 'bg-emerald-100 text-emerald-700',
  validator_teacher_code: 'bg-emerald-100 text-emerald-700',
  structural_pattern: 'bg-amber-100 text-amber-700',
  validated_pattern: 'bg-sky-100 text-sky-700',
  fallback: 'bg-gray-100 text-gray-600',
  // Normalized source colors (from origin.source)
  header: 'bg-violet-100 text-violet-700',
  row_label: 'bg-teal-100 text-teal-700',
  cell_content: 'bg-amber-100 text-amber-700',
  cell_time: 'bg-cyan-100 text-cyan-700',
  filename: 'bg-gray-100 text-gray-600',
  markdown: 'bg-sky-100 text-sky-700',
  template: 'bg-purple-100 text-purple-700',
  derived: 'bg-gray-100 text-gray-500',
}

const ORIGIN_SOURCE_COLORS: Record<string, string> = {
  header: 'bg-violet-100 text-violet-700',
  row_label: 'bg-teal-100 text-teal-700',
  cell_content: 'bg-amber-100 text-amber-700',
  cell_time: 'bg-cyan-100 text-cyan-700',
  filename: 'bg-gray-100 text-gray-600',
  markdown: 'bg-sky-100 text-sky-700',
  template: 'bg-purple-100 text-purple-700',
  derived: 'bg-gray-100 text-gray-500',
}

function getConfidenceBadge(confidence: number): { label: string; color: string } {
  if (confidence >= 0.95) return { label: 'Hohe', color: 'bg-green-100 text-green-700' }
  if (confidence >= 0.80) return { label: 'Mittel', color: 'bg-yellow-100 text-yellow-700' }
  return { label: 'Niedrig', color: 'bg-orange-100 text-orange-700' }
}

interface TokenMatchingModalProps {
  isOpen: boolean
  sessionId?: string
  templateId?: string
  matchedTokens: TokenMatch[]
  unmatchedTokens: UnmatchedToken[]
  tokenPositions: Record<string, TokenPositions>
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>
  splitTracking: Record<string, string>
  unmatchedHeaderCodes?: string[]
  onResolve: (resolutions: Array<{ token: string; field: string; confirmed: boolean; split?: boolean; split_into?: string[]; auto_split?: boolean }>, resolutionContexts?: TokenResolutionContext[]) => void
  onCancel: () => void
  isResolving?: boolean
  batchInfo?: BatchInfo
  onPause?: () => void
}

export function TokenMatchingModal({
  isOpen,
  sessionId,
  templateId,
  matchedTokens,
  unmatchedTokens,
  tokenPositions,
  orphanPatterns,
  splitTracking,
  unmatchedHeaderCodes = [],
  onResolve,
  onCancel,
  isResolving = false,
  batchInfo,
  onPause,
}: TokenMatchingModalProps) {
  const [activeTab, setActiveTab] = useState<'matched' | 'confirmed' | 'unmatched' | 'ignored'>('confirmed')
  const [searchFilter, setSearchFilter] = useState('')
  const [fieldDialog, setFieldDialog] = useState<{ token: string; tokens: string[] } | null>(null)
  const [editingToken, setEditingToken] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [splitDialog, setSplitDialog] = useState<{ isOpen: boolean; token: string; batchTokens: string[] }>({ isOpen: false, token: '', batchTokens: [] })
  const [multiSplit, setMultiSplit] = useState<{ isOpen: boolean; tokens: string[] }>({ isOpen: false, tokens: [] })
  const [statusMessage, setStatusMessage] = useState('')
  const [flashTokens, setFlashTokens] = useState<Set<string>>(new Set())
  const [freshSplitTokens, setFreshSplitTokens] = useState<string[]>([])
  const [combineDialog, setCombineDialog] = useState<{ isOpen: boolean; tokens: string[] }>({
    isOpen: false,
    tokens: [],
  })
  const editInputRef = useRef<HTMLInputElement>(null)

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false)
  const [resolutionDialogToken, setResolutionDialogToken] = useState<string | null>(null)
  const [resolutionDialogTokens, setResolutionDialogTokens] = useState<string[]>([])
  const [resolutionDialogType, setResolutionDialogType] = useState<'subject' | 'background'>('subject')
  const [notification, setNotification] = useState<{ type: 'error' | 'success' | 'info'; message: string } | null>(null)
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false)
  const [undoLoading, setUndoLoading] = useState(false)
  const [hasResolutions, setHasResolutions] = useState(false)
  const [showUnmatchedCodeDialog, setShowUnmatchedCodeDialog] = useState(false)
  const [pendingUnmatchedCodes, setPendingUnmatchedCodes] = useState<string[]>([])
  const [knownTokens, setKnownTokens] = useState<Record<string, { 
    field: string
    confirmed: boolean
    last_action?: string
    last_batch_id?: string
    last_at?: string
    source?: string
    split_into?: string[]
    is_split?: boolean
    auto_split?: boolean
  }>>({})
  const [knownTokensReady, setKnownTokensReady] = useState(false)
  const openKey = isOpen ? `${sessionId ?? ''}|${templateId ?? ''}` : ''
  const readyKeyRef = useRef('')
  if (readyKeyRef.current !== openKey) readyKeyRef.current = ''
  const [subjectIndex, setSubjectIndex] = useState<Map<string, string | null>>(new Map())
  const autoContinueRef = useRef(false)
  const skipAutoContinueRef = useRef(false)
  const pendingPartAssignmentsRef = useRef<Array<{ token: string; field: Exclude<PartFieldId, 'subject'> }> | null>(null)
  const subjectQueueRef = useRef<string[]>([])
  const subjectAdvanceRef = useRef(false)
  const unmatchedTabInitRef = useRef(false)
  const autoSplitSeen = useRef(new Set<string>())
  const queuedMultiSplits = useRef<Array<{ token: string; parts: string[] }>>([])

  const [usePagination, setUsePagination] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const [pdfPreview, setPdfPreview] = useState<PdfPageResponse | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const resolutionWorkflow = useTokenResolutionWorkflow()
  const openTimeRef = useRef<number>(0)

  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = performance.now()
    }
    return () => {
      openTimeRef.current = 0
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      autoContinueRef.current = false
      skipAutoContinueRef.current = false
      unmatchedTabInitRef.current = false
      autoSplitSeen.current.clear()
      readyKeyRef.current = ''
      setKnownTokensReady(false)
      setSearchFilter('')
      setCurrentPage(1)
      return undefined
    }
    const key = `${sessionId ?? ''}|${templateId ?? ''}`
    readyKeyRef.current = ''
    setKnownTokensReady(false)
    setSearchFilter('')
    setCurrentPage(1)
    let cancelled = false
    const subjectTimer = setTimeout(() => {
      Promise.all([
        listSubjects().catch(() => []),
        listSubjectAliases().catch(() => []),
      ]).then(([subjects, aliases]) => {
        if (!cancelled) setSubjectIndex(buildSubjectIdIndex(subjects, aliases))
      })
    }, 100)
    setSubjectIndex(new Map())
    autoContinueRef.current = false
    skipAutoContinueRef.current = false
    const namesPromise = loadIgnoredTokenNames({ refresh: true }).catch(() => knownIgnoredTokenNames())
    const statusPromise = templateId
      ? timetableImportV2Api.getTokenStatus(templateId).then((data) => data.token_status || {}).catch(() => ({}))
      : Promise.resolve({} as Record<string, { field: string; confirmed: boolean }>)
    void Promise.all([namesPromise, statusPromise]).then(([names, status]) => {
      if (cancelled) return
      setKnownTokens(overlayIgnoredNames(status, [...names, ...knownIgnoredTokenNames()]))
    }).finally(() => {
      if (!cancelled) {
        readyKeyRef.current = key
        setKnownTokensReady(true)
      }
    })
    return () => {
      cancelled = true
      clearTimeout(subjectTimer)
    }
  }, [isOpen, templateId, sessionId])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, searchFilter])

  const filteredMatchedTokens = useMemo(() => {
    if (import.meta.env.DEV) {
      console.log('[TokenMatchingModal] filteredMatchedTokens useMemo - matchedTokens:', matchedTokens.length, 'knownTokens:', Object.keys(knownTokens).length)
      if (matchedTokens.length > 0) {
        console.log('[TokenMatchingModal] Sample matchedTokens:', matchedTokens.slice(0, 3).map(mt => ({ token: mt.token, field: mt.field })))
      }
    }
    const result: TokenMatch[] = []
    let confirmedApplied = 0
    let splitApplied = 0
    const matchedTokenKeys = new Set<string>()
    
    // First, process existing matchedTokens
    for (const mt of matchedTokens) {
      const tokenKey = mt.token.toLowerCase()
      matchedTokenKeys.add(tokenKey)
      // Case-insensitive lookup: knownTokens keys are lowercase
      const status = knownTokenStatus(mt.token, knownTokens) || knownTokens[tokenKey]
      if (status?.field === 'ignore' || mt.field === 'ignore' || isIgnoredToken(mt.token, knownIgnoredTokenNames())) {
        result.push({
          ...mt,
          field: 'ignore',
          auto_confirmed: true,
          wasPreviouslyConfirmed: true,
          is_split: Boolean(status?.is_split || mt.is_split || (status?.split_into || mt.split_into || []).length >= 2),
          auto_split: Boolean(status?.auto_split || mt.auto_split),
          split_into: status?.split_into || mt.split_into,
        })
        continue
      }
      
      // CRITICAL: Check if this token was previously split - auto-resolve it
      // Check both from knownTokens (status) and from matchedTokens (mt)
      const isSplitToken = (status?.field === 'split' && status.split_into) || 
                           (mt.is_split && mt.split_into)
      const splitParts = status?.split_into || mt.split_into
      
      if (isSplitToken && splitParts) {
        splitApplied++
        if (import.meta.env.DEV) {
          console.log(`[TokenMatchingModal] Auto-resolving split token '${mt.token}' into ${splitParts.length} parts:`, splitParts)
        }
        
        // NEW v3: Do NOT add the original token – only the parts
        // Add each part as a matched token with split visualization
        for (const part of splitParts) {
          const partKey = part.toLowerCase()
          if (matchedTokenKeys.has(partKey)) {
            continue // part already processed
          }
          const partStatus = knownTokenStatus(part, knownTokens)
          if (partStatus?.field && partStatus.field !== 'ignore' && partStatus.field !== 'split') {
            matchedTokenKeys.add(partKey)
            result.push({
              token: part,
              field: partStatus.field,
              confidence: 0.95,
              positions: mt.positions,
              pattern_source: 'template_split',
              auto_confirmed: true,
              wasPreviouslyConfirmed: true,
              split_into: splitParts,     // All parts for visualization
            })
          }
        }
        continue // Skip further processing for this token
      }
      
      // Check if token is confirmed either from knownTokens OR from wasPreviouslyConfirmed
      const isConfirmed = (status?.confirmed && status?.field) || mt.wasPreviouslyConfirmed === true
      if (isConfirmed) {
        confirmedApplied++
        result.push({
          ...mt,
          field: status?.field || mt.field,
          confidence: Math.max(mt.confidence, 0.95),
          auto_confirmed: true,
          wasPreviouslyConfirmed: true,
        })
      } else {
        result.push(mt)
      }
    }
    
    // CRITICAL: Add confirmed tokens from knownTokens that aren't in matchedTokens
    // This ensures tokens confirmed in previous sessions are included
    for (const [tokenKey, status] of Object.entries(knownTokens)) {
      if (matchedTokenKeys.has(tokenKey)) continue
      // Skip split tokens - they are handled above
      if (status?.field === 'split' || status?.field === 'ignore') continue
      if (status?.confirmed && status?.field) {
        confirmedApplied++
        // Add this token as a matched token
        result.push({
          token: tokenKey,
          field: status.field,
          confidence: 0.95,
          positions: { cols: [], rows: [], count: 0 },
          pattern_source: 'token_history',
          auto_confirmed: true,
          wasPreviouslyConfirmed: true,
        })
      }
    }
    
    if (import.meta.env.DEV) {
      console.log('[TokenMatchingModal] filteredMatchedTokens result:', {
        inputMatched: matchedTokens.length,
        inputKnown: Object.keys(knownTokens).length,
        confirmedApplied,
        splitApplied,
        outputResult: result.length,
        autoConfirmedCount: result.filter(r => r.auto_confirmed).length,
        sampleAutoConfirmed: result.filter(r => r.auto_confirmed).slice(0, 5).map(r => ({ token: r.token, field: r.field }))
      })
    }
    
    return result
  }, [matchedTokens, knownTokens])

  const filteredUnmatchedTokens = useMemo(() => {
    const result: UnmatchedToken[] = []
    for (const ut of unmatchedTokens) {
      // Case-insensitive lookup: knownTokens keys are lowercase
      const status = knownTokenStatus(ut.token, knownTokens)
      if (status?.field === 'ignore' || isIgnoredToken(ut.token, knownIgnoredTokenNames())) {
        continue
      }
      // CRITICAL: Skip tokens that were previously split - they are auto-resolved
      if (status?.field === 'split') {
        continue
      }
      if (status?.confirmed && status?.field) {
        // Token was previously confirmed - skip from unmatched
        continue
      }
      result.push(ut)
    }
    return result
  }, [unmatchedTokens, knownTokens])

  const initialMatched = useMemo(() => {
    const m: Record<string, { 
      field: string
      confidence: number
      positions?: TokenPositions
      source?: string | null
      pattern?: string | null
      origin?: TokenOrigin | null
      has_canonical_link?: boolean | null
      canonical_subject_id?: string | null
      teacher_exists?: boolean | null
      is_tentative?: boolean
      auto_confirmed?: boolean
      wasPreviouslyConfirmed?: boolean
      is_split?: boolean
      auto_split?: boolean
      split_into?: string[]
    }> = {}
    for (const mt of filteredMatchedTokens) {
      m[mt.token] = { 
        field: mt.field, 
        confidence: mt.confidence, 
        positions: mt.positions,
        source: mt.pattern_source || null,
        pattern: null,
        origin: mt.origin || null,
        has_canonical_link: mt.has_canonical_link ?? null,
        canonical_subject_id: mt.canonical_subject_id ?? null,
        teacher_exists: mt.teacher_exists ?? null,
        is_tentative: mt.is_tentative ?? false,
        auto_confirmed: mt.auto_confirmed || shouldAutoConfirmVeryHighConfidence(mt) || false,
        wasPreviouslyConfirmed: mt.wasPreviouslyConfirmed ?? false,
        is_split: mt.is_split ?? false,
        auto_split: mt.auto_split ?? false,
        split_into: mt.split_into ?? undefined,
      }
    }
      if (import.meta.env.DEV) {
        console.log('[TokenMatchingModal] initialMatched built:', {
          total: Object.keys(m).length,
          autoConfirmed: Object.values(m).filter(v => v.auto_confirmed).length,
          sampleAutoConfirmed: Object.entries(m)
            .filter(([_, v]) => v.auto_confirmed)
            .slice(0, 5)
            .map(([k, v]) => ({ token: k, field: v.field }))
        })
      }
    const savedIgnores = new Map<string, string>()
    for (const [key, status] of Object.entries(knownTokens)) {
      if (status?.field === 'ignore' && key.trim()) {
        savedIgnores.set(key.toLowerCase(), key)
      }
    }
    for (const name of knownIgnoredTokenNames()) {
      const token = name.trim()
      if (token) savedIgnores.set(token.toLowerCase(), savedIgnores.get(token.toLowerCase()) || token)
    }
    for (const ut of unmatchedTokens) {
      if (knownTokenStatus(ut.token, knownTokens)?.field === 'ignore') savedIgnores.set(ut.token.toLowerCase(), ut.token)
    }
    for (const token of savedIgnores.values()) {
      if (Object.keys(m).some((item) => item.toLowerCase() === token.toLowerCase())) continue
      const fromFile = unmatchedTokens.find((item) => item.token.toLowerCase() === token.toLowerCase())
      const known = knownTokenStatus(token, knownTokens)
      m[token] = {
        field: 'ignore',
        confidence: 1,
        positions: fromFile?.positions || { cols: [], rows: [], count: 0 },
        source: 'previously_ignored',
        pattern: null,
        origin: fromFile?.origin || null,
        has_canonical_link: null,
        canonical_subject_id: null,
        teacher_exists: null,
        is_tentative: false,
        auto_confirmed: true,
        wasPreviouslyConfirmed: true,
        is_split: Boolean(known?.is_split || (known?.split_into || []).length >= 2),
        auto_split: Boolean(known?.auto_split),
        split_into: known?.split_into,
      }
    }
      return m
  }, [filteredMatchedTokens, unmatchedTokens, knownTokens])

  const initialUnmatched = useMemo(() => {
    const m: Record<string, { positions?: TokenPositions; origin?: TokenOrigin | null }> = {}
    for (const ut of filteredUnmatchedTokens) {
      m[ut.token] = {
        positions: ut.positions,
        origin: ut.origin || null,
      }
    }
    return m
  }, [filteredUnmatchedTokens])

  const {
    state,
    selectedTokensList,
    toggleSelect,
    clearSelection,
    assignAndConfirm,
    assignAndConfirmMany,
    unassign,
    ignore,
    batchIgnore,
    unignore,
    split,
    batchSplit,
    splitMany,
    applyAssignedSplits,
    selectSimilar,
    combine,
    combineWithSimilar,
    getResolutions,
    confirmToken,
    confirmTokens,
    unconfirmToken,
    isConfirmed,
    suggestField,
    undo,
    reset,
    renameToken,
    renameAndConfirm,
  } = useTokenStore(initialMatched, initialUnmatched, orphanPatterns, tokenPositions, splitTracking)

  const splitConfirmed = useMemo(() => {
    const out: ConfirmedToken[] = []
    for (const [token, status] of Object.entries(knownTokens)) {
      if (status?.confirmed && status.field && status.field !== 'ignore') out.push({ text: token, field: status.field })
    }
    for (const entry of Object.values(state.matched)) {
      if (entry.confirmed && entry.field && entry.field !== 'ignore') out.push({ text: entry.token, field: entry.field })
    }
    return out
  }, [knownTokens, state.matched])

  useEffect(() => {
    if (!isOpen || !knownTokensReady) return
    const pending = Object.keys(state.unmatched).filter((token) => !autoSplitSeen.current.has(token))
    if (!pending.length) return
    let cancelled = false
    void Promise.all([loadSplitHints(), loadIgnoredTokenNames().catch(() => knownIgnoredTokenNames())])
      .then(([loaded, names]) => {
        if (cancelled) return
        const loadedHints = withConfirmed(loaded, splitConfirmed)
        const hints = {
          ...loadedHints,
          confirmed: (loadedHints.confirmed || []).filter((item) => item.field !== 'ignore'),
        }
        const items = []
        for (const token of pending) {
          if (isIgnoredToken(token, names)) {
            ignore(token)
            autoSplitSeen.current.add(token)
            continue
          }
          const split = assignedSplit(token, hints)
          if (!split) {
            autoSplitSeen.current.add(token)
            continue
          }
          const fields = split.fields.map((field, index) => (
            isIgnoredToken(split.parts[index] || '', names) ? 'ignore' : field
          ))
          items.push({ token, parts: split.parts, fields, subjectIds: split.subjectIds })
        }
        if (items.length) applyAssignedSplits(items, names)
      })
      .catch(() => {
        for (const token of pending) autoSplitSeen.current.add(token)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, knownTokensReady, state.unmatched, splitConfirmed, applyAssignedSplits, ignore])

  const releaseIgnored = useCallback((tokens: string[]) => {
    const unique = [...new Set(tokens.map((token) => token.trim()).filter(Boolean))]
    if (!unique.length) return
    forgetIgnoredTokens(unique)
    setKnownTokens((prev) => {
      const next = { ...prev }
      for (const token of unique) {
        const key = Object.keys(next).find((item) => isIgnoredToken(item, [token]) || isIgnoredToken(token, [item]))
        if (!key || next[key]?.field !== 'ignore') continue
        delete next[key]
      }
      return next
    })
    for (const token of unique) {
      void deleteTokenShop(token).catch(() => {})
    }
  }, [])

  const persistIgnored = useCallback((tokens: string[]) => {
    const unique = [...new Set(tokens.map((token) => token.trim()).filter(Boolean))]
    if (!unique.length) return
    rememberIgnoredTokens(unique)
    setKnownTokens((prev) => overlayIgnoredNames(prev, unique))
    const save = timetableImportV2Api.rememberIgnoredTokens?.(unique, templateId)
    if (!save) return
    void save.catch(() => {
      setNotification({ type: 'error', message: 'Ignorieren wurde nicht gespeichert' })
    })
  }, [templateId])

  const applyDeferredPartAssignments = useCallback(() => {
    const pending = pendingPartAssignmentsRef.current
    pendingPartAssignmentsRef.current = null
    if (!pending?.length) return
    const ignoreTokens = pending.filter((row) => row.field === 'ignore').map((row) => row.token)
    for (const row of pending) {
      if (row.field === 'ignore') continue
      assignAndConfirm(row.token, row.field)
    }
    if (ignoreTokens.length) {
      skipAutoContinueRef.current = true
      batchIgnore(ignoreTokens)
      persistIgnored(ignoreTokens)
    }
  }, [assignAndConfirm, batchIgnore, persistIgnored])

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Log state.matched after initialization
  useEffect(() => {
    if (Object.keys(state.matched).length > 0) {
      const confirmedCount = Object.keys(state.matched).filter(t => state.matched[t]?.confirmed).length
      console.log('[TokenMatchingModal] state.matched initialized:', {
        total: Object.keys(state.matched).length,
        confirmed: confirmedCount,
        sampleConfirmed: Object.entries(state.matched)
          .filter(([_, v]) => v.confirmed)
          .slice(0, 5)
          .map(([k, v]) => ({ token: k, field: v.field }))
      })
    }
  }, [state.matched])

  const canUndo = state.history.length > 0

  // Phase 1: Flush wrong -> unmatched on data load
  useEffect(() => {
    if (Object.keys(state.wrong).length > 0) {
      for (const token of Object.keys(state.wrong)) {
        unassign(token)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMatched, initialUnmatched])

  // Check qualification matrix status on modal open
  useEffect(() => {
    if (isOpen) {
      resolutionWorkflow.checkQualificationMatrix()
      resolutionWorkflow.reset()
      setPdfPreview(null)
      setPdfViewerOpen(false)
      setNotification(null)
      setHasResolutions(false)
      // Check for unmatched header teacher codes
      if (unmatchedHeaderCodes.length > 0) {
        setPendingUnmatchedCodes(unmatchedHeaderCodes)
        setShowUnmatchedCodeDialog(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Track when resolutions are added
  useEffect(() => {
    if (resolutionWorkflow.pendingResolutions.size > 0) {
      setHasResolutions(true)
    }
  }, [resolutionWorkflow.pendingResolutions.size])

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [notification])

  // Auto-confirm tokens with auto_confirmed flag from API
  // For template-sourced tokens, skip confidence check (template is source of truth)
  // Also auto-confirm based on origin matching
  useEffect(() => {
    let autoConfirmedCount = 0
    let originAutoConfirmCount = 0
    
    // Load session confirmations for origin-based matching
    const sessionConfirmations: Record<string, { confirmed: boolean; confirmedField: string | null }> = {}
    try {
      const data = sessionStorage.getItem(getConfirmedStorageKey(sessionId))
      if (data) {
        const parsed = JSON.parse(data)
        for (const [key, value] of Object.entries(parsed)) {
          sessionConfirmations[key] = value as { confirmed: boolean; confirmedField: string | null }
        }
      }
    } catch {
      // ignore
    }
    
    for (const mt of filteredMatchedTokens) {
      const wasPreviouslyConfirmed = mt.wasPreviouslyConfirmed === true
      const isAutoConfirmed = mt.auto_confirmed === true
      const isTemplateSourced = mt.pattern_source?.startsWith('template_')
      
      // Origin-based auto-confirm
      const origin = (mt.origin ?? null) as TokenOrigin | null
      const originAutoConfirm = shouldAutoConfirmByOrigin(
        mt.token,
        origin,
        knownTokens,
        sessionConfirmations
      )
      
      const shouldAutoConfirm = isAutoConfirmed || 
        (wasPreviouslyConfirmed && (isTemplateSourced || mt.confidence >= 1.0)) ||
        originAutoConfirm ||
        shouldAutoConfirmVeryHighConfidence(mt)
      
      if (shouldAutoConfirm && !isConfirmed(mt.token)) {
        confirmToken(mt.token)
        autoConfirmedCount++
        if (originAutoConfirm) originAutoConfirmCount++
      }
    }
    if (autoConfirmedCount > 0) {
      const originMsg = originAutoConfirmCount > 0 
        ? ` (${originAutoConfirmCount} per Kontext)` 
        : ''
      setStatusMessage(`${autoConfirmedCount} Token automatisch bestätigt (aus Template${originMsg})`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMatchedTokens, knownTokens])

  const startEditing = useCallback((token: string) => {
    setEditingToken(token)
    setEditValue(token)
    setTimeout(() => editInputRef.current?.focus(), 50)
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingToken(null)
    setEditValue('')
  }, [])

  const finishEdit = useCallback((token: string) => {
    const newToken = editValue.trim()
    if (!newToken || newToken === token) {
      cancelEdit()
      return
    }

    const entry = stateRef.current.matched[token] || stateRef.current.wrong[token] || stateRef.current.unmatched[token] || stateRef.current.ignored[token]
    const originalField = entry?.field ?? null

    const others = selectedTokensList.filter(t => t !== token)

    const resolvedField = originalField || suggestField(newToken)?.field || null
    renameAndConfirm(token, newToken, resolvedField, true)

    for (const otherToken of others) {
      const transformed = applyBatchEdit(otherToken, token, newToken)
      if (transformed && transformed !== otherToken) {
        renameToken(otherToken, transformed)
      }
    }

    setStatusMessage(`Bearbeitet: ${token} → ${newToken}${others.length > 0 ? ` (+${others.length} ausgewählt)` : ''}`)
    setFlashTokens(prev => new Set(prev).add(newToken))
    setTimeout(() => setFlashTokens(new Set()), 600)
    cancelEdit()
    clearSelection()
  }, [editValue, selectedTokensList, renameAndConfirm, renameToken, suggestField, cancelEdit, clearSelection])

  const handleAssign = useCallback((token: string, field: string) => {
    if (field === 'ignore') skipAutoContinueRef.current = true
    assignAndConfirm(token, field)
    setStatusMessage(`${token} → ${FIELD_LABELS[field] || field} (bestätigt)`)
    setFieldDialog(null)
    clearSelection()
  }, [assignAndConfirm, clearSelection])

  const showSubjectToken = useCallback((token: string) => {
    const hasMatrix = resolutionWorkflow.qualificationMatrixStatus?.exists && !resolutionWorkflow.qualificationMatrixStatus?.is_empty
    setResolutionDialogToken(token)
    setResolutionDialogTokens([token])
    setResolutionDialogType(hasMatrix ? 'subject' : 'background')
    setResolutionDialogOpen(true)
    setFieldDialog(null)
  }, [resolutionWorkflow.qualificationMatrixStatus])

  const openSubjectResolution = useCallback((tokens: string[]) => {
    if (!sessionId) {
      setNotification({ type: 'error', message: 'Keine Session-ID verfügbar' })
      return
    }

    const unique = [...new Set(tokens.filter(Boolean))]
    if (unique.length === 0) return

    const [first, ...rest] = unique
    subjectQueueRef.current = rest
    showSubjectToken(first!)
  }, [sessionId, showSubjectToken])

  const handleSubjectAssign = useCallback(async (token: string) => {
    openSubjectResolution([token])
  }, [openSubjectResolution])

  const handleBatchSubjectAssign = useCallback(async (tokens: string[]) => {
    openSubjectResolution(tokens)
  }, [openSubjectResolution])

  // Handler for subject resolution after combining tokens
  const handleSubjectResolutionAfterCombine = useCallback((combinedToken: string) => {
    if (!sessionId) {
      setNotification({ type: 'error', message: 'Keine Session-ID verfügbar' })
      return
    }

    // Combine the selected tokens into the new token (without field - will be assigned after resolution)
    if (combineDialog.tokens.length >= 2) {
      combine(combineDialog.tokens, combinedToken, null)
    }

    // Close the combine dialog and open resolution dialog
    setCombineDialog({ isOpen: false, tokens: [] })

    openSubjectResolution([combinedToken])
  }, [sessionId, combine, combineDialog.tokens, openSubjectResolution])

  const handleResolutionComplete = useCallback(async (resolutionType: 'alias' | 'new_subject' | 'new_background' | 'exact_match', data: Record<string, unknown>) => {
    const current = resolutionDialogToken
    if (!current) return

    applyDeferredPartAssignments()

    const canonicalSubjectId = (data.canonical_subject_id || data.subject_id) as string | undefined
    const tokenEntry = state.matched[current] || state.unmatched[current] || state.wrong[current] || state.ignored[current]
    if (resolutionType !== 'exact_match') {
      const context: TokenResolutionContext = {
        token: current,
        resolution_type: resolutionType as ResolutionType,
        resolution_data: {
          ...data,
          alias_name: current,
          tokens: [current],
          canonical_subject_id: canonicalSubjectId,
          subject_id: canonicalSubjectId || data.subject_id,
        },
        origin: (tokenEntry?.origin ?? null) as Record<string, unknown> | null,
      }
      resolutionWorkflow.addResolution(current, context)
    }
    assignAndConfirm(current, 'subject', { canonical_subject_id: canonicalSubjectId })
    toggleSelect(current, false)

    const next = subjectQueueRef.current.shift()
    const doneLabel = resolutionType === 'exact_match'
      ? `${current} → Fach "${data.subject_name}" ✓ Direkt zugewiesen`
      : `${current} → Fach ${resolutionType === 'alias' ? '(Alias erstellt)' : '(Neu erstellt)'} ✓ bestätigt`
    if (next) {
      subjectAdvanceRef.current = true
      setStatusMessage(`${doneLabel} · noch ${subjectQueueRef.current.length + 1}`)
      showSubjectToken(next)
      return
    }

    setStatusMessage(doneLabel)
    setResolutionDialogOpen(false)
    setResolutionDialogToken(null)
    setResolutionDialogTokens([])
    setFieldDialog(null)
    setFreshSplitTokens([])
    clearSelection()
  }, [resolutionDialogToken, resolutionWorkflow, assignAndConfirm, toggleSelect, clearSelection, showSubjectToken, state.matched, state.unmatched, state.wrong, state.ignored, applyDeferredPartAssignments])

  const canViewPdf = Boolean(sessionId || templateId)

  const handlePdfView = useCallback(async (token?: string) => {
    console.log('[TokenMatchingModal] handlePdfView called, sessionId:', sessionId, 'templateId:', templateId, 'token:', token)
    if (!sessionId && !templateId) {
      console.log('[TokenMatchingModal] No sessionId or templateId, showing error')
      setNotification({ type: 'error', message: 'Keine Session-ID verfügbar' })
      return
    }

    const pos = token
      ? (tokenPositions[token] || Object.entries(tokenPositions).find(([key]) => key.toLowerCase() === token.toLowerCase())?.[1])
      : undefined
    const sourceSession = pos?.sessions?.length ? pos.sessions[pos.sessions.length - 1] : undefined

    setPdfLoading(true)
    try {
      const response = await loadTokenPdf({
        token,
        sessionId,
        templateId,
        sourceSessionId: sourceSession,
      })
      if (response?.pdf_base64) {
        setPdfPreview(response)
        setPdfViewerOpen(true)
      } else {
        setNotification({ type: 'error', message: 'PDF konnte nicht geladen werden' })
      }
    } catch (err) {
      console.error('[TokenMatchingModal] PDF load error:', err)
      setNotification({ type: 'error', message: 'PDF konnte nicht geladen werden' })
    } finally {
      setPdfLoading(false)
    }
  }, [sessionId, templateId, tokenPositions])

  const handleApply = useCallback(() => {
    if (isResolving) return
    const resolutions = getResolutions()
    const pendingResolutions = resolutionWorkflow.getPendingResolutions()
    
    // Debug: log what's being sent (always log, not just in DEV)
    console.log('[TokenMatchingModal] handleApply:', {
      resolutionsCount: resolutions.length,
      confirmedCount: resolutions.filter(r => r.confirmed).length,
      splitCount: resolutions.filter(r => r.split).length,
      pendingResolutionsCount: pendingResolutions.length,
      sampleResolutions: resolutions.slice(0, 3),
    })
    
    onResolve(resolutions, pendingResolutions.length > 0 ? pendingResolutions : undefined)
  }, [getResolutions, onResolve, isResolving, resolutionWorkflow])

  useEffect(() => {
    if (!isOpen || !knownTokensReady || unmatchedTabInitRef.current) return
    unmatchedTabInitRef.current = true
    if (Object.keys(state.unmatched).length > 0 || Object.keys(state.wrong).length > 0) {
      setActiveTab('unmatched')
    }
  }, [isOpen, knownTokensReady, state.unmatched, state.wrong])

  useEffect(() => {
    if (!isOpen || !knownTokensReady || isResolving || showUnmatchedCodeDialog) return
    if (readyKeyRef.current !== `${sessionId ?? ''}|${templateId ?? ''}`) return
    const propsCount = matchedTokens.length + unmatchedTokens.length
    const storeCount = Object.keys(state.matched).length + Object.keys(state.unmatched).length + Object.keys(state.ignored).length + Object.keys(state.wrong).length
    if (propsCount > 0 && storeCount === 0) return
    if (!sessionId) return
    if (autoContinueRef.current || skipAutoContinueRef.current) return
    const unconfirmed = Object.keys(state.matched).filter((t) => !state.matched[t]?.confirmed)
    if (unconfirmed.length > 0) return
    if (Object.keys(state.unmatched).length > 0 || Object.keys(state.wrong).length > 0) return
    autoContinueRef.current = true
    handleApply()
  }, [isOpen, sessionId, templateId, knownTokensReady, isResolving, showUnmatchedCodeDialog, matchedTokens.length, unmatchedTokens.length, state.matched, state.unmatched, state.ignored, state.wrong, handleApply])

  const handleUndo = useCallback(async () => {
    if (!sessionId) {
      setNotification({ type: 'error', message: 'Keine Session-ID verfügbar' })
      return
    }

    setUndoLoading(true)
    try {
      const result = await tokenResolutionApi.undoImportSession(sessionId)
      setNotification({ type: 'success', message: `${result.deleted_aliases} Aliase, ${result.deleted_subjects} Fächer, ${result.deleted_backgrounds} Hintergründe gelöscht` })
      resolutionWorkflow.reset()
      reset()
      setActiveTab('unmatched')
      setHasResolutions(false)
      setUndoConfirmOpen(false)
    } catch (err) {
      setNotification({ type: 'error', message: 'Undo fehlgeschlagen' })
    } finally {
      setUndoLoading(false)
    }
  }, [sessionId, resolutionWorkflow, reset])

  const handleSplitApply = useCallback((parts: string[], splitIndices: number[], _separator: string, editedToken: string) => {
    const primaryToken = splitDialog.token
    const others = splitDialog.batchTokens
    void (async () => {
    const ignoredNames = await loadIgnoredTokenNames().catch(() => knownIgnoredTokenNames())
    const saved = enrichSavedSubjects(knownTokens, subjectIndex)
    const splitResult = split(primaryToken, parts, ignoredNames, saved)
    const keptParts = splitResult.keptParts || []
    const unlinked = new Set(splitResult.unlinkedSubjects)
    const ask = parts.filter((part) => !keptParts.includes(part) && !unlinked.has(part) && !isIgnoredToken(part, ignoredNames))
    const keptInfo = keptParts.length > 0 ? ` (${keptParts.join(', ')} schon zugeordnet)` : ''
    const matchInfo = splitResult.matchedParts.length > 0 ? ` (${splitResult.matchedParts.join(', ')} erkannt)` : ''
    const unmatchedInfo = splitResult.unmatchedParts.length > 0 ? ` (${splitResult.unmatchedParts.join(', ')} offen)` : ''
    setStatusMessage(`Geteilt: ${primaryToken} → ${parts.join(', ')}${keptInfo}${matchInfo}${unmatchedInfo}`)
    if (others.length > 0) {
      const result = batchSplit(editedToken, parts, splitIndices, others, ignoredNames, saved)
      for (const token of result.unlinkedSubjects) unlinked.add(token)
      const skippedMsg = result.skipped > 0 ? ` (${result.skipped} übersprungen)` : ''
      setStatusMessage(prev => prev + ` (+${others.length}${skippedMsg})`)
      const fresh = result.newTokens.filter((token) => !keptParts.includes(token) && !unlinked.has(token))
      clearSelection()
      fresh.forEach(t => toggleSelect(t, true))
      setFreshSplitTokens(fresh)
    } else {
      clearSelection()
      ask.forEach(t => toggleSelect(t, true))
      setFreshSplitTokens(ask)
    }
    setFlashTokens(new Set(parts))
    setTimeout(() => setFlashTokens(new Set()), 600)
    setSplitDialog({ isOpen: false, token: '', batchTokens: [] })
    if (unlinked.size) openSubjectResolution([...unlinked])
    })()
  }, [splitDialog.token, splitDialog.batchTokens, split, batchSplit, clearSelection, toggleSelect, knownTokens, subjectIndex, openSubjectResolution])

  const handleMultiSplitApply = useCallback((results: { token: string; parts: string[] }[], options?: { close?: boolean; revoke?: string[] }) => {
    const pending = results.filter((row) => row.parts.length >= 2)
    const ready = mergeSplitQueue(queuedMultiSplits.current, pending, options?.revoke, (row) => [row.token])
    if (options?.close === false) {
      queuedMultiSplits.current = ready
      return
    }
    queuedMultiSplits.current = []
    void (async () => {
      if (ready.length > 0) {
        const ignoredNames = await loadIgnoredTokenNames().catch(() => knownIgnoredTokenNames())
        const saved = enrichSavedSubjects(knownTokens, subjectIndex)
        const { newTokens, unlinkedSubjects } = splitMany(ready, ignoredNames, saved)
        setStatusMessage(`${ready.length} Token geteilt`)
        clearSelection()
        newTokens.filter((token) => !unlinkedSubjects.includes(token)).forEach((token) => toggleSelect(token, true))
        setFreshSplitTokens(newTokens.filter((token) => !unlinkedSubjects.includes(token)))
        if (unlinkedSubjects.length) openSubjectResolution(unlinkedSubjects)
        setFlashTokens(new Set(newTokens))
        setTimeout(() => setFlashTokens(new Set()), 600)
      }
      setMultiSplit({ isOpen: false, tokens: [] })
    })()
  }, [splitMany, clearSelection, toggleSelect, knownTokens, subjectIndex, openSubjectResolution])

  const handleSimilarSelect = useCallback((sourceToken: string) => {
    const allSimilar = selectSimilar(sourceToken)
    const tokensInTab = new Set<string>()
    for (const t of Object.keys(tabTokens[activeTab])) tokensInTab.add(t)
    const similar = allSimilar.filter(t => tokensInTab.has(t))
    clearSelection()
    similar.forEach(t => toggleSelect(t, true))
    const tabLabel = activeTab === 'matched' ? 'Zugeordnet' : activeTab === 'confirmed' ? 'Bestätigt' : activeTab === 'ignored' ? 'Ignoriert' : 'Nicht zugeordnet'
    setStatusMessage(`${similar.length} ähnliche Token gewählt (${tabLabel}, Schwelle: ${state.config.threshold.toFixed(2)})`)
  }, [selectSimilar, clearSelection, toggleSelect, state.config.threshold, activeTab, state.matched, state.unmatched, state.ignored])

  const handleSelectAll = useCallback(() => {
    let tokens: string[]
    if (activeTab === 'matched') {
      tokens = Object.keys(state.matched).filter(t => !state.matched[t]?.confirmed && state.matched[t]?.field !== 'ignore')
    } else if (activeTab === 'confirmed') {
      tokens = Object.keys(state.matched).filter(t => state.matched[t]?.confirmed && state.matched[t]?.field !== 'ignore')
    } else if (activeTab === 'ignored') {
      tokens = Object.keys(state.ignored)
    } else {
      tokens = [...Object.keys(state.unmatched), ...Object.keys(state.wrong)]
    }
    clearSelection()
    tokens.forEach(t => toggleSelect(t, true))
  }, [activeTab, state.matched, state.unmatched, state.wrong, state.ignored, clearSelection, toggleSelect])

  const handleBatchAssign = useCallback((field: string) => {
    if (field === 'ignore') skipAutoContinueRef.current = true
    assignAndConfirmMany(selectedTokensList, field)
    setStatusMessage(`${selectedTokensList.length} Token als "${FIELD_LABELS[field] || field}" zugeordnet und bestätigt`)
    clearSelection()
    setFieldDialog(null)
  }, [selectedTokensList, assignAndConfirmMany, clearSelection])

  // Phase 2: Unmatch handler
  const handleUnmatch = useCallback((token: string) => {
    const others = selectedTokensList.filter(t => t !== token && state.matched[t])
    if (others.length > 0) {
      for (const t of [token, ...others]) {
        unassign(t)
      }
      setStatusMessage(`${others.length + 1} Token entfernt`)
    } else {
      unassign(token)
      setStatusMessage(`${token} entfernt`)
    }
    clearSelection()
  }, [selectedTokensList, state.matched, unassign, clearSelection])

  const getSplitLookup = useCallback((token: string): SplitPartLookup | undefined => {
    const entry = state.matched[token] || state.unmatched[token] || state.wrong[token] || state.ignored[token]
    if (!entry) return undefined
    return {
      splitFrom: entry.splitFrom,
      field: entry.ignored || entry.field === 'ignore' ? 'ignore' : entry.field,
      compoundParent: entry.origin?.structural?.compound_parent ?? null,
    }
  }, [state.matched, state.unmatched, state.wrong, state.ignored])

  const showPartAssign = useMemo(
    () => selectedTokensList.some((token) => freshSplitTokens.includes(token)),
    [selectedTokensList, freshSplitTokens],
  )

  useEffect(() => {
    if (freshSplitTokens.length === 0) return
    if (selectedTokensList.some((token) => freshSplitTokens.includes(token))) return
    setFreshSplitTokens([])
  }, [selectedTokensList, freshSplitTokens])

  const partGroups = useMemo(
    () => groupSplitParts(selectedTokensList, getSplitLookup, state.splits || {}),
    [selectedTokensList, getSplitLookup, state.splits],
  )

  const handleDrawerSplit = useCallback(() => {
    const splittable = selectedTokensList.filter(canSplitToken)
    if (splittable.length > 1) {
      setMultiSplit({ isOpen: true, tokens: splittable })
      return
    }
    const primary = splittable[0]
    if (!primary) return
    if (hasSplitPoints(primary)) {
      setSplitDialog({ isOpen: true, token: primary, batchTokens: [] })
    } else {
      setMultiSplit({ isOpen: true, tokens: [primary] })
    }
  }, [selectedTokensList])

  const handleApplyPartAssignments = useCallback((assignments: Array<{ token: string; field: PartFieldId }>) => {
    const subjects: string[] = []
    const deferred: Array<{ token: string; field: Exclude<PartFieldId, 'subject'> }> = []
    for (const { token, field } of assignments) {
      if (field === 'subject') {
        const known = state.matched[token]
        const pending = resolutionWorkflow.pendingResolutions.get(token)
        const canonical = known?.canonical_subject_id || pending?.resolution_data?.canonical_subject_id
        if (known?.confirmed && canonical) {
          assignAndConfirm(token, 'subject', { canonical_subject_id: String(canonical) })
          continue
        }
        subjects.push(token)
      } else deferred.push({ token, field })
    }
    pendingPartAssignmentsRef.current = deferred
    applyDeferredPartAssignments()
    if (subjects.length && sessionId) {
      setStatusMessage(`${deferred.length + subjects.length} Zuordnungen`)
      openSubjectResolution(subjects)
      return
    }
    for (const token of subjects) {
      assignAndConfirm(token, 'subject')
    }
    setStatusMessage(`${assignments.length} Zuordnungen übernommen`)
    setFreshSplitTokens([])
    clearSelection()
  }, [assignAndConfirm, sessionId, openSubjectResolution, clearSelection, applyDeferredPartAssignments, state.matched, resolutionWorkflow.pendingResolutions])

  // Phase 3: Combine handler — opens similarity dialog
  const openCombineDialog = useCallback((token: string) => {
    let tokensToCombine = selectedTokensList.includes(token)
      ? selectedTokensList
      : [token, ...selectedTokensList]
    if (tokensToCombine.length < 2) {
      setStatusMessage('Wähle mindestens 2 Token zum Verbinden aus')
      return
    }
    setCombineDialog({
      isOpen: true,
      tokens: tokensToCombine,
    })
  }, [selectedTokensList])

  const workflowStep = state.config.workflowStep
  const tabTokens = useMemo(() => {
    const matchedUnconfirmed: Record<string, typeof state.matched[string]> = {}
    const matchedConfirmed: Record<string, typeof state.matched[string]> = {}
    for (const [token, entry] of Object.entries(state.matched)) {
      if (!entry || entry.field === 'ignore') continue
      if (entry.confirmed) matchedConfirmed[token] = entry
      else matchedUnconfirmed[token] = entry
    }
    return {
      matched: matchedUnconfirmed,
      confirmed: matchedConfirmed,
      unmatched: { ...state.unmatched, ...state.wrong },
      ignored: state.ignored,
    }
  }, [state.matched, state.unmatched, state.wrong, state.ignored])

  const openUnmatchedCount = Object.keys(tabTokens.unmatched).length
  const hasUnresolved = openUnmatchedCount > 0

  const filterTokens = useMemo(() => {
    return (tokens: Record<string, typeof state.matched[string]>) => {
      if (!searchFilter) return Object.entries(tokens)
      const lower = searchFilter.toLowerCase()
      return Object.entries(tokens).filter(([token]) => token.toLowerCase().includes(lower))
    }
  }, [searchFilter])

  const isTimeSlot = (token: string) => /^\d{1,2}:\d{2}[-–]?\d{1,2}:\d{2}$/.test(token)
  const isSingleChar = (token: string) => /^[A-Za-z]$/.test(token)

  // Phase 4: Uniform renderTokenItem
  const renderTokenItem = (token: string, entry: typeof state.matched[string], type: 'matched' | 'unmatched' | 'ignored') => {
    const posBadge = (entry?.positions?.count ?? 0) > 0 ? `×${entry.positions.count}` : ''
    const isSelected = selectedTokensList.includes(token)
    const isEditing = editingToken === token
    const isFlashing = flashTokens.has(token)
    const isIgnored = type === 'ignored'

    return (
      <div
        key={token}
        className={`flex flex-wrap items-center gap-2 p-2 border-b text-sm ${isIgnored && !entry.auto_split && !entry.is_split ? 'opacity-60' : ''} ${entry.auto_split || entry.is_split ? 'bg-amber-50/60' : ''} ${isSelected ? 'bg-orange-50' : ''} ${isFlashing ? 'animate-pulse bg-yellow-50' : ''}`}
        onDoubleClick={(e) => { e.preventDefault(); startEditing(token) }}
      >
        <button
          className={`text-xs px-1.5 py-0.5 rounded border flex-shrink-0 ${isSelected ? 'bg-orange-100 border-orange-300 text-orange-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}
          onClick={() => toggleSelect(token, !isSelected)}
        >
          {isSelected ? '✓' : '○'}
        </button>
        {isEditing ? (
          <input ref={editInputRef} type="text" className="font-mono text-xs bg-yellow-50 border border-orange-300 px-1.5 py-0.5 rounded outline-none flex-1" value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finishEdit(token) } if (e.key === 'Escape') { e.preventDefault(); cancelEdit() } }} onBlur={() => finishEdit(token)} />
        ) : (
          <code className="block max-w-full whitespace-normal break-all font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded text-gray-900" title={getOriginDescription(entry.origin) || token}>{token}</code>
        )}
        {posBadge && <span className="text-xs text-gray-500">{posBadge}</span>}
        {entry.field && (<Badge className={`text-xs ${FIELD_COLORS[entry.field] || 'bg-gray-100 text-gray-800'}`}>{entry.field === 'time_slot' && <Clock className="h-3 w-3 mr-0.5 inline" />}{FIELD_LABELS[entry.field] || entry.field}</Badge>)}
        {entry.confidence && entry.field && entry.field !== 'ignore' && (
          <Badge className={`text-xs ${getConfidenceBadge(entry.confidence).color}`}>{getConfidenceBadge(entry.confidence).label}</Badge>
        )}
        {entry.field === 'teacher_code' && entry.teacher_exists === false && (
          <Badge className="text-xs bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-0.5 inline" />Nicht in DB</Badge>
        )}
        {entry.field === 'subject' && entry.has_canonical_link === false && (
          <Badge className="text-xs bg-amber-100 text-amber-700"><AlertCircle className="h-3 w-3 mr-0.5 inline" />Kein Fach in DB</Badge>
        )}
        {entry.is_tentative && (
          <Badge className="text-xs bg-orange-100 text-orange-700"><AlertCircle className="h-3 w-3 mr-0.5 inline" />Vorläufig</Badge>
        )}
        {entry.splitFrom && entry.split_into && (
          <Badge className="text-xs bg-orange-100 text-orange-700">
            ✓ Teil von: {entry.splitFrom} (Geteilt in: {entry.split_into.join(', ')})
          </Badge>
        )}
        {!entry.splitFrom && entry.is_split && entry.split_into && (
          <Badge className="text-xs bg-orange-100 text-orange-700">
            ✓ Gespalten in: {entry.split_into.join(', ')}
          </Badge>
        )}
        {entry.source && entry.field && entry.field !== 'ignore' && SOURCE_LABELS[entry.source] && (
          <Badge className={`text-xs ${SOURCE_COLORS[entry.source] || 'bg-gray-100 text-gray-600'}`}>{SOURCE_LABELS[entry.source]}</Badge>
        )}
        {entry.origin && entry.field && entry.field !== 'ignore' && (
          <Badge className={`text-xs ${ORIGIN_SOURCE_COLORS[entry.origin.source || ''] || 'bg-gray-100 text-gray-600'}`}>
            {getOriginLabel(entry.origin)}
          </Badge>
        )}
        {isIgnored && (<Badge className="text-xs bg-gray-200 text-gray-600">Ignoriert</Badge>)}
        {entry.auto_split && (
          <Badge className="text-xs bg-amber-100 text-amber-800" title="Automatisch geteilt. Prüfen, ob die Teile stimmen.">Autogesplittet</Badge>
        )}
        {entry.is_split && !entry.auto_split && (
          <Badge className="text-xs bg-amber-100 text-amber-800" title="Bereits geteilt.">Geteilt</Badge>
        )}
        {!entry.field && type === 'unmatched' && (() => {
          const teacherFields = new Set(['teacher_code', 'teacher_name', 'first_name', 'family_name'])
          const sugField = suggestField(token)?.field
          const backendField = (entry as any).suggestions?.[0]?.field
          const fieldToAssign = (sugField && !teacherFields.has(sugField) ? sugField : null)
            || (backendField && !teacherFields.has(backendField) ? backendField : null)
          if (!fieldToAssign) return null
          return (
          <button className="text-xs text-blue-500 hover:underline" onClick={() => { 
            if (fieldToAssign === 'subject' && sessionId) { 
              handleSubjectAssign(token) 
            } else { 
              handleAssign(token, fieldToAssign) 
            }
          }}>
            [{fieldToAssign}]
          </button>
          )
        })()}
        <div className="ml-auto flex gap-1 flex-wrap">
          {isConfirmed(token) && (
            <Badge className="text-xs bg-green-100 text-green-700">
              ✓ Bestätigt
              {entry.originConfirmed?.[getOriginKey(entry.origin)] && (
                <span className="ml-1 text-[10px] opacity-75">(Kontext)</span>
              )}
            </Badge>
          )}
          {canViewPdf && (
            <button className="text-xs px-2 py-0.5 hover:bg-blue-50 rounded border border-blue-200 text-blue-700" onClick={() => handlePdfView(token)} title="PDF anzeigen">
              <Eye className="h-3 w-3 inline mr-0.5" />
              PDF
            </button>
          )}
          <button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-200" onClick={() => startEditing(token)}>Bearbeiten</button>
          <button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-200" onClick={() => setFieldDialog({ token, tokens: selectedTokensList.includes(token) ? selectedTokensList : [token] })}>Zuordnen</button>
          {(type === 'unmatched' || type === 'matched') && entry.field !== 'ignore' && (
            <button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-300 text-gray-600" onClick={() => { skipAutoContinueRef.current = true; ignore(token); persistIgnored([token]); setStatusMessage(`${token} bestätigt als Ignorieren`) }}>Ignorieren</button>
          )}
          <button className="text-xs px-2 py-0.5 hover:bg-blue-50 rounded border border-blue-200 text-blue-700" onClick={handleSelectAll}>Alle auswählen</button>
          <button className="text-xs px-2 py-0.5 hover:bg-red-50 rounded border border-red-200 text-red-700" onClick={clearSelection}>Alle abwählen</button>
          {type === 'matched' && entry.field !== 'ignore' && (isConfirmed(token)
            ? (<button className="text-xs px-2 py-0.5 hover:bg-yellow-50 rounded border border-yellow-200 text-yellow-700" onClick={() => { unconfirmToken(token); setStatusMessage(`${token} unbestätigt`); clearSelection() }}>Unbestätigen</button>)
            : (<button className="text-xs px-2 py-0.5 hover:bg-green-50 rounded border border-green-200 text-green-700" onClick={() => {
                const tokensToConfirm = selectedTokensList.includes(token) && selectedTokensList.length > 1
                  ? selectedTokensList.filter(t => state.matched[t] && state.matched[t]?.field)
                  : [token]
                const r = tokensToConfirm.length > 1 ? confirmTokens(tokensToConfirm) : confirmToken(token)
                const msg = r.extracted > 0 ? `${tokensToConfirm.length} Token bestätigt, ${r.extracted} extrahiert` : `${tokensToConfirm.length} Token bestätigt`
                setStatusMessage(msg)
                clearSelection()
              }}>Bestätigen</button>)
          )}
          {type === 'matched' && (<button className="text-xs px-2 py-0.5 hover:bg-red-50 rounded border border-red-200 text-red-700" onClick={() => handleUnmatch(token)}>Unmatch</button>)}
          {(type === 'ignored' || entry.field === 'ignore') && (<button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-200" onClick={() => { unignore(token); releaseIgnored([token]); setStatusMessage(`${token} wiederhergestellt`) }}>Wiederherstellen</button>)}
          <button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-200" onClick={() => openCombineDialog(token)}>Verbinden</button>
          {!isEditing && !isTimeSlot(token) && !isSingleChar(token) && !entry.is_split && (
            <button className="text-xs px-2 py-0.5 hover:bg-blue-50 rounded border border-blue-200 text-blue-700" onClick={() => {
              const batch = (selectedTokensList.includes(token) ? selectedTokensList : [token]).filter((item) => canSplitToken(item) && !isTimeSlot(item))
              if (batch.length > 1) {
                setMultiSplit({ isOpen: true, tokens: batch })
                return
              }
              if (hasSplitPoints(token)) setSplitDialog({ isOpen: true, token, batchTokens: [] })
              else setMultiSplit({ isOpen: true, tokens: [token] })
            }}>Teilen</button>
          )}
          {entry.is_split && (
            <span className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-400 cursor-not-allowed" title="Bereits aufgeteilt">Teilen</span>
          )}
          <button className="text-xs px-2 py-0.5 hover:bg-gray-100 rounded border border-gray-200" onClick={() => handleSimilarSelect(token)}>Ähnlich auswählen</button>
        </div>
      </div>
    )
  }

  const renderTabContent = (type: 'matched' | 'confirmed' | 'unmatched' | 'ignored') => {
    const tokens = tabTokens[type]
    const filtered = filterTokens(tokens)
    if (filtered.length === 0) {
      if (type === 'unmatched' && openUnmatchedCount > 0) {
        return (
          <div className="text-center py-8 text-gray-500 space-y-2">
            <p>{searchFilter ? `Keine Treffer für «${searchFilter}».` : 'Offene Token nicht sichtbar.'}</p>
            <p>{openUnmatchedCount} Token bleiben offen.</p>
            {searchFilter ? (
              <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => setSearchFilter('')}>
                Filter löschen
              </button>
            ) : null}
          </div>
        )
      }
      return (<div className="text-center py-8 text-gray-500">{type === 'matched' && 'Keine zugeordneten Token'}{type === 'confirmed' && 'Keine bestätigten Token'}{type === 'ignored' && 'Keine ignorierten Token'}{type === 'unmatched' && 'Alle Token verarbeitet!'}</div>)
    }
    const grouped = filtered.reduce<Record<string, typeof filtered>>((acc, [token, entry]) => {
      const key = entry?.field || 'unassigned'
      if (!acc[key]) acc[key] = []
      acc[key].push([token, entry])
      return acc
    }, {})

    const flatEntries = Object.entries(grouped).flatMap(([field, entries]) =>
      entries.map(([token, entry]) => ({ token, entry, field }))
    )
    const virtualRows = Object.entries(grouped).flatMap(([field, entries]) => [
      { kind: 'header' as const, field, count: entries.length },
      ...entries.map(([token, entry]) => ({ kind: 'token' as const, token, entry, field })),
    ])

    const renderGroupedHeader = (field: string, count: number) => (
      <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 text-xs font-medium">
        {field !== 'unassigned' ? (<Badge className={`text-xs ${FIELD_COLORS[field] || 'bg-gray-100 text-gray-800'}`}>{FIELD_LABELS[field] || field}</Badge>) : (<span className="text-gray-500">Nicht zugeordnet</span>)}
        <span className="text-gray-500">{count}</span>
      </div>
    )

    if (usePagination && flatEntries.length > pageSize) {
      const start = (currentPage - 1) * pageSize
      const end = start + pageSize
      const pageEntries = flatEntries.slice(start, end)

      const pageGrouped = pageEntries.reduce<Record<string, typeof pageEntries>>((acc, { token, entry, field }) => {
        if (!acc[field]) acc[field] = []
        acc[field].push({ token, entry, field })
        return acc
      }, {})

      return (
        <>
          <div className="max-h-96 overflow-y-auto">
            {Object.entries(pageGrouped).map(([field, entries]) => (
              <div key={field} className="mb-2">
                {renderGroupedHeader(field, entries.length)}
                {entries.map(({ token, entry }) => renderTokenItem(token, entry, type === 'confirmed' ? 'matched' : type))}
              </div>
            ))}
          </div>
          <TokenPagination
            total={flatEntries.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </>
      )
    }

    if (flatEntries.length > 200) {
      return (
        <VirtualizedTokenList
          tokens={virtualRows}
          getItemKey={(index) => {
            const row = virtualRows[index]
            if (!row) return index
            return row.kind === 'header' ? `header:${row.field}` : `token:${row.field}:${row.token}`
          }}
          renderToken={(row) => (
            row.kind === 'header'
              ? renderGroupedHeader(row.field, row.count)
              : renderTokenItem(row.token, row.entry, type === 'confirmed' ? 'matched' : type)
          )}
          estimateSize={(index) => (virtualRows[index]?.kind === 'header' ? 28 : 160)}
          height="400px"
        />
      )
    }

    return (
      <div className="max-h-96 overflow-y-auto">
        {Object.entries(grouped).map(([field, entries]) => (
          <div key={field} className="mb-2">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 text-xs font-medium sticky top-0">
              {field !== 'unassigned' ? (<Badge className={`text-xs ${FIELD_COLORS[field] || 'bg-gray-100 text-gray-800'}`}>{FIELD_LABELS[field] || field}</Badge>) : (<span className="text-gray-500">Nicht zugeordnet</span>)}
              <span className="text-gray-500">{entries.length}</span>
            </div>
            {entries.map(([token, entry]) => renderTokenItem(token, entry, type === 'confirmed' ? 'matched' : type))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Token-Zuordnung" size="xl">
      <div className="flex gap-0 max-h-[80vh]">
        {/* Main content area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Batch Info Header - only if batchInfo provided */}
          {batchInfo && (
            <BatchInfoHeader
              batchInfo={batchInfo}
              onPause={onPause}
            />
          )}
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 py-2">
              {(['review', 'unmatched', 'save'] as const).map((step, idx) => {
                const stepLabels = ['Prüfen', 'Zuordnen', 'Abschliessen']
                const isActive = workflowStep === step || (step === 'unmatched' && workflowStep === 'fix')
                const isCompleted = (step === 'review' && Object.keys(state.wrong).length === 0 && Object.keys(state.unmatched).length === 0) || (step === 'unmatched' && Object.keys(state.unmatched).length === 0) || (step === 'save' && !hasUnresolved)
                return (
                  <div key={step} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{idx + 1}</div>
                    <span className={`text-xs ${isActive ? 'font-medium' : 'text-gray-500'}`}>{stepLabels[idx]}</span>
                    {idx < 2 && <div className="w-4 h-px bg-gray-300" />}
                  </div>
                )
              })}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Token filtern..." className="pl-8" value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={usePagination}
                  onChange={e => { setUsePagination(e.target.checked); setCurrentPage(1) }}
                  className="rounded border-gray-300"
                />
                Seiten
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const restored = undo()
                  if (!restored?.tokens.length) return
                  if (restored.action === 'ignore') releaseIgnored(restored.tokens)
                  setStatusMessage('')
                  if (restored.bucket === 'matched') setActiveTab('confirmed')
                  else setActiveTab('unmatched')
                  setFlashTokens(new Set(restored.tokens))
                  setTimeout(() => setFlashTokens(new Set()), 600)
                }}
                disabled={!canUndo}
                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                  canUndo
                    ? 'bg-amber-500 text-white hover:bg-amber-600 cursor-pointer'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={canUndo ? 'Letzte Aktion rückgängig' : 'Keine Aktion zum Rückgängigmachen'}
              >
                <Undo2 className="w-4 h-4" />
                Undo
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  console.log('[TokenMatchingModal] PDF button clicked, sessionId:', sessionId, 'templateId:', templateId, 'pdfViewerOpen:', pdfViewerOpen)
                  if (pdfViewerOpen) {
                    setPdfViewerOpen(false)
                  } else {
                    handlePdfView()
                  }
                }}
                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                  canViewPdf
                    ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={canViewPdf ? 'PDF-Vorschau öffnen' : 'Keine Session-ID'}
                disabled={!canViewPdf}
              >
                <FileText className="w-4 h-4" />
                {pdfLoading ? 'Laden...' : (pdfViewerOpen ? 'PDF schliessen' : 'PDF anzeigen')}
              </button>
            </div>

            {statusMessage && (<div className="text-sm text-gray-600 italic">{statusMessage}</div>)}

            {!knownTokensReady ? <div className="py-8 text-center text-sm text-gray-500">Ignorierte Tokens werden geladen…</div> : null}
            <div className={`flex border-b ${knownTokensReady ? '' : 'hidden'}`}>
              {([['matched', 'Zugeordnet'], ['confirmed', 'Bestätigt'], ['unmatched', 'Nicht zugeordnet'], ['ignored', 'Ignoriert']] as const).map(([tab, label]) => {
                const count = Object.keys(tabTokens[tab]).length
                const countColor = tab === 'matched' ? 'text-green-600 font-bold' : tab === 'confirmed' ? 'text-emerald-600 font-bold' : tab === 'ignored' ? 'text-gray-600 font-bold' : 'text-red-600 font-bold'
                return (<button key={tab} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => { setActiveTab(tab); clearSelection() }}>{label} (<span className={countColor}>{count}</span>)</button>)
              })}
            </div>

            {knownTokensReady && activeTab === 'matched' && renderTabContent('matched')}
            {knownTokensReady && activeTab === 'confirmed' && renderTabContent('confirmed')}
            {knownTokensReady && activeTab === 'unmatched' && renderTabContent('unmatched')}
            {knownTokensReady && activeTab === 'ignored' && renderTabContent('ignored')}

            {fieldDialog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFieldDialog(null)}>
                <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                  <h3 className="font-medium mb-2">Feldtyp zuordnen</h3>
                  <code className="block bg-gray-100 px-3 py-2 rounded text-center mb-4 font-mono">{fieldDialog.tokens.length > 1 ? `${fieldDialog.tokens.length} Token` : fieldDialog.token}</code>
                  <div className="space-y-1 mb-4">
                    {Object.entries(FIELD_LABELS).map(([field, label]) => (<button key={field} className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-sm ${FIELD_COLORS[field] || ''}`} onClick={() => { if (field === 'subject' && sessionId) { if (fieldDialog.tokens.length > 1) { handleBatchSubjectAssign(fieldDialog.tokens) } else { handleSubjectAssign(fieldDialog.token) } } else { if (fieldDialog.tokens.length > 1) { handleBatchAssign(field) } else { handleAssign(fieldDialog.token, field) } } }}>{label}</button>))}
                  </div>
                  <Button variant="secondary" className="w-full" onClick={() => setFieldDialog(null)}>Abbrechen</Button>
                </div>
              </div>
            )}

            <SplitDialog isOpen={splitDialog.isOpen} token={splitDialog.token} selectedTokens={[splitDialog.token, ...splitDialog.batchTokens]} orphanPatterns={state.config.orphanPatterns} confirmed={splitConfirmed} onApply={handleSplitApply} onCancel={() => setSplitDialog({ isOpen: false, token: '', batchTokens: [] })} />
            <MultiSplitDialog isOpen={multiSplit.isOpen} tokens={multiSplit.tokens} confirmed={splitConfirmed} onApply={handleMultiSplitApply} onCancel={() => { queuedMultiSplits.current = []; setMultiSplit({ isOpen: false, tokens: [] }) }} />

            <CombineSimilarDialog
              isOpen={combineDialog.isOpen}
              sourceTokens={combineDialog.tokens}
              allUnmatchedTokens={Object.keys(state.unmatched)}
              allMatchedTokens={Object.keys(state.matched)}
              onCombineWithSimilar={combineWithSimilar}
              onSubjectResolutionNeeded={handleSubjectResolutionAfterCombine}
              onCancel={() => setCombineDialog({ isOpen: false, tokens: [] })}
            />
          </div>
          </div>  {/* Close overflow-y-auto container */}
          
          {/* Sticky Bottom Action Bar - NEW */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {Object.keys(state.matched).length} zugeordnet · {openUnmatchedCount} offen
                </span>
                {hasResolutions && sessionId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setUndoConfirmOpen(true)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Undo
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={onCancel}>Abbrechen</Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => {
                    // Pause action - close modal and save state
                    onCancel()
                  }}
                >
                  ⏸ Pause
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleApply} 
                  disabled={isResolving || hasUnresolved}
                >
                  {hasUnresolved ? 'Offene Token zuerst zuordnen' : 'Abschliessen'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Slide-in Selection Panel */}
        <div className={`overflow-hidden transition-all duration-200 ease-out border-l border-gray-200 bg-gray-50 flex flex-col ${
          selectedTokensList.length > 0 ? (showPartAssign ? 'w-[360px] opacity-100' : 'w-52 opacity-100') : 'w-0 opacity-0'
        }`}>
          <div className="p-2 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-semibold text-gray-700">{showPartAssign ? 'Teile zuordnen' : `${selectedTokensList.length} ausgewählt`}</span>
            <div className="flex gap-1">
              <button className="text-xs px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm" onClick={handleSelectAll}>Alle</button>
              <button className="text-xs px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm" onClick={clearSelection}>Keine</button>
            </div>
          </div>

          {showPartAssign ? (
            <SplitPartAssignPanel
              groups={partGroups}
              onApply={handleApplyPartAssignments}
              onCancel={clearSelection}
              onSplit={handleDrawerSplit}
              canSplit={selectedTokensList.some(canSplitToken)}
            />
          ) : (
            <>
              {selectedTokensList.length > 0 && (
                <div className="p-2 border-b border-gray-200 flex flex-col gap-1 flex-shrink-0">
                  <button
                    className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
                    onClick={() => setFieldDialog({ token: selectedTokensList[0]!, tokens: selectedTokensList })}
                  >
                    Zuordnen
                  </button>
                  <button
                    className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
                    onClick={() => {
                      const tokensToConfirm = selectedTokensList.filter(t => state.matched[t] && state.matched[t]?.field)
                      if (tokensToConfirm.length > 0) {
                        confirmTokens(tokensToConfirm)
                        setStatusMessage(`${tokensToConfirm.length} Token bestätigt`)
                        clearSelection()
                      }
                    }}
                    disabled={!selectedTokensList.some(t => state.matched[t] && state.matched[t]?.field)}
                  >
                    Bestätigen
                  </button>
                  <button
                    className="w-full text-xs px-2 py-1.5 rounded bg-white border border-red-200 text-red-700 hover:bg-red-50 shadow-sm"
                    onClick={() => {
                      for (const t of selectedTokensList) {
                        if (state.matched[t]) handleUnmatch(t)
                      }
                      clearSelection()
                    }}
                    disabled={!selectedTokensList.some(t => state.matched[t])}
                  >
                    Unmatch
                  </button>
                  <button
                    className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
                    onClick={handleDrawerSplit}
                    disabled={!selectedTokensList.some(canSplitToken)}
                  >
                    Teilen
                  </button>
                  <button
                    className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 shadow-sm"
                    onClick={() => {
                      const toIgnore = selectedTokensList.filter(t => state.unmatched[t] || state.wrong[t] || state.matched[t])
                      if (toIgnore.length > 0) {
                        skipAutoContinueRef.current = true
                        batchIgnore(toIgnore)
                        persistIgnored(toIgnore)
                        setStatusMessage(`${toIgnore.length} Token bestätigt als Ignorieren`)
                        clearSelection()
                      }
                    }}
                    disabled={!selectedTokensList.some(t => state.unmatched[t] || state.wrong[t] || state.matched[t])}
                  >
                    Ignorieren
                  </button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {selectedTokensList.map(token => {
                  const entry = state.matched[token] || state.unmatched[token] || state.wrong[token] || state.ignored[token]
                  const fieldColor = entry?.field ? FIELD_COLORS[entry.field] : 'bg-gray-100 text-gray-700'
                  return (
                    <div key={token} className={`relative px-2 py-1 pr-5 rounded text-xs w-full ${fieldColor}`}>
                      <code className="block w-full whitespace-normal break-all font-mono text-gray-900" title={token}>{token}</code>
                      <button
                        className="absolute top-1 right-1 text-gray-400 hover:text-red-500 font-bold leading-none"
                        onClick={() => toggleSelect(token, false)}
                        title="Abwählen"
                      >
                        ×
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {notification && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
          notification.type === 'error' ? 'bg-red-100 text-red-800 border border-red-300' :
          notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' :
          'bg-blue-100 text-blue-800 border border-blue-300'
        }`}>
          {notification.type === 'error' && <AlertCircle className="h-4 w-4" />}
          <span className="text-sm">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="text-gray-500 hover:text-gray-700 ml-2">×</button>
        </div>
      )}

      {pdfViewerOpen && pdfPreview?.pdf_base64 && (
        <PdfPageViewer
          pdfBase64={pdfPreview.pdf_base64}
          pageNumber={pdfPreview.page_number}
          totalPages={pdfPreview.total_pages}
          subtitle={[pdfPreview.section_identifier, pdfPreview.source_filename].filter(Boolean).join(' · ') || undefined}
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
        />
      )}

      {resolutionDialogOpen && resolutionDialogToken && (
        resolutionDialogType === 'subject' ? (
          <UnmatchedSubjectDialog
            key={resolutionDialogToken}
            open={resolutionDialogOpen}
            token={resolutionDialogToken}
            tokens={[resolutionDialogToken]}
            hasQualificationMatrix={(resolutionWorkflow.qualificationMatrixStatus?.exists ?? false) && !(resolutionWorkflow.qualificationMatrixStatus?.is_empty ?? true)}
            sessionId={sessionId}
            templateId={templateId}
            positions={(() => {
              const tokens = resolutionDialogTokens.length ? resolutionDialogTokens : [resolutionDialogToken]
              const sessions: string[] = []
              const pages: number[] = []
              for (const name of tokens) {
                const pos = tokenPositions[name] || Object.entries(tokenPositions).find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1]
                for (const sid of pos?.sessions || []) {
                  if (!sessions.includes(sid)) sessions.push(sid)
                }
                for (const page of pos?.pages || []) {
                  if (!pages.includes(page)) pages.push(page)
                }
              }
              return sessions.length || pages.length ? { sessions, pages } : undefined
            })()}
            onResolve={handleResolutionComplete}
            onClose={() => {
              if (subjectAdvanceRef.current) {
                subjectAdvanceRef.current = false
                return
              }
              subjectQueueRef.current = []
              pendingPartAssignmentsRef.current = null
              setResolutionDialogOpen(false)
              setResolutionDialogToken(null)
              setResolutionDialogTokens([])
            }}
          />
        ) : (
          <UnmatchedBackgroundDialog
            key={resolutionDialogToken}
            open={resolutionDialogOpen}
            token={resolutionDialogToken}
            tokens={[resolutionDialogToken]}
            sessionId={sessionId}
            onResolve={handleResolutionComplete}
            onClose={() => {
              if (subjectAdvanceRef.current) {
                subjectAdvanceRef.current = false
                return
              }
              subjectQueueRef.current = []
              pendingPartAssignmentsRef.current = null
              setResolutionDialogOpen(false)
              setResolutionDialogToken(null)
              setResolutionDialogTokens([])
            }}
          />
        )
      )}

      <UnmatchedTeacherCodeDialog
        isOpen={showUnmatchedCodeDialog}
        unmatchedCodes={pendingUnmatchedCodes}
        onAssign={async (code, _teacherId) => {
          setShowUnmatchedCodeDialog(false)
          setNotification({ type: 'success', message: `Code "${code}" wurde zugewiesen. Bitte erneut extrahieren.` })
          // Remove the assigned code from pending unmatched codes
          const remaining = pendingUnmatchedCodes.filter(c => c !== code)
          setPendingUnmatchedCodes(remaining)
          // If there are more unmatched codes, show the dialog again
          if (remaining.length > 1) {
            setShowUnmatchedCodeDialog(true)
          }
        }}
        onSkip={() => {
          setShowUnmatchedCodeDialog(false)
          setNotification({ type: 'info', message: 'Seite ohne Lehrer-Code wird fortgesetzt.' })
        }}
      />

      {undoConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setUndoConfirmOpen(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-medium mb-2 text-lg">Undo bestätigen</h3>
            <p className="text-sm text-gray-600 mb-4">
              Alle in dieser Session erstellten Aliase, Fächer und Hintergründe werden gelöscht.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setUndoConfirmOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleUndo}
                disabled={undoLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {undoLoading ? 'Laden...' : 'Undo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
